import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';
import {
  Save, Plus, Trash2, ChevronDown, ChevronUp, Users,
  ClipboardCheck, Shield, AlertTriangle, FileText
} from 'lucide-react';

const EXCLUSIONARY_FACTORS = [
  { key: 'lack_instruction', label: 'Lack of appropriate instruction in reading and/or math' },
  { key: 'educational_disadvantage', label: 'Educational disadvantages' },
  { key: 'economic_disadvantage', label: 'Economic disadvantages' },
  { key: 'environmental_disadvantage', label: 'Environmental disadvantages' },
  { key: 'hearing_vision', label: 'Hearing or vision deficits' },
  { key: 'limited_english', label: 'Limited English language proficiency and/or a non-native English speaker' },
  { key: 'cultural_disadvantage', label: 'Cultural disadvantages' },
];

const TEAM_ROLES = [
  'SLP', 'School Psychologist', 'General Education Teacher', 'Special Education Teacher',
  'Occupational Therapist', 'Physical Therapist', 'District Representative',
  'Parent 1', 'Parent 2', 'Other',
];

const CONSENT_OPTIONS = ['authorized', 'denied', 'pending', 'withdrawn'];

function buildDefaults() {
  const ef = {};
  EXCLUSIONARY_FACTORS.forEach(f => { ef[f.key] = { value: '', clarification: '' }; });
  return {
    exclusionary_factors: ef,
    additional_data_needed: '',
    proposed_areas: [],
    parental_consent: { status: null, date: '', notes: '' },
    procedural_safeguards: { provided: false, date: '', method: '' },
    evaluation_review_team: { participants: [] },
  };
}

function mergeContent(existing) {
  const defaults = buildDefaults();
  if (!existing || typeof existing !== 'object') return defaults;
  const merged = JSON.parse(JSON.stringify(defaults));
  if (existing.exclusionary_factors && typeof existing.exclusionary_factors === 'object') {
    EXCLUSIONARY_FACTORS.forEach(f => {
      if (existing.exclusionary_factors[f.key] && typeof existing.exclusionary_factors[f.key] === 'object') {
        merged.exclusionary_factors[f.key] = { ...merged.exclusionary_factors[f.key], ...existing.exclusionary_factors[f.key] };
      }
    });
  }
  if (existing.additional_data_needed) merged.additional_data_needed = existing.additional_data_needed;
  if (existing.proposed_areas) merged.proposed_areas = existing.proposed_areas;
  if (existing.parental_consent) merged.parental_consent = { ...merged.parental_consent, ...existing.parental_consent };
  if (existing.procedural_safeguards) merged.procedural_safeguards = { ...merged.procedural_safeguards, ...existing.procedural_safeguards };
  if (existing.evaluation_review_team) merged.evaluation_review_team = { ...merged.evaluation_review_team, ...existing.evaluation_review_team };
  return merged;
}

function CollapsibleGroup({ title, children, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button className="flex items-center justify-between w-full mb-3 px-1" onClick={() => setOpen(!open)}>
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959]">{title}</h3>
        {open ? <ChevronUp className="h-3.5 w-3.5 text-[#595959]" /> : <ChevronDown className="h-3.5 w-3.5 text-[#595959]" />}
      </button>
      {open && children}
    </div>
  );
}

export default function PermissionToEvaluateEditor({ section, caseData, onSave, saving }) {
  const [content, setContent] = useState(() => mergeContent(section?.content));
  useEffect(() => { setContent(mergeContent(section?.content)); }, [section?.section_id]);

  const handleSave = () => onSave(content);

  const update = (path, value) => {
    setContent(prev => {
      const next = JSON.parse(JSON.stringify(prev));
      let ref = next;
      for (let i = 0; i < path.length - 1; i++) {
        if (!ref[path[i]]) ref[path[i]] = {};
        ref = ref[path[i]];
      }
      ref[path[path.length - 1]] = value;
      return next;
    });
  };

  // Proposed areas
  const addArea = () => update(['proposed_areas'], [...(content.proposed_areas || []), '']);
  const removeArea = (idx) => update(['proposed_areas'], content.proposed_areas.filter((_, i) => i !== idx));
  const updateArea = (idx, value) => {
    const updated = content.proposed_areas.map((a, i) => i === idx ? value : a);
    update(['proposed_areas'], updated);
  };

  // Team participants
  const addParticipant = () => {
    update(['evaluation_review_team', 'participants'], [
      ...(content.evaluation_review_team?.participants || []),
      { id: `p_${Date.now()}`, name: '', role: '', title: '' },
    ]);
  };
  const removeParticipant = (idx) => {
    update(['evaluation_review_team', 'participants'],
      content.evaluation_review_team.participants.filter((_, i) => i !== idx));
  };
  const updateParticipant = (idx, field, value) => {
    const updated = content.evaluation_review_team.participants.map((p, i) => i === idx ? { ...p, [field]: value } : p);
    update(['evaluation_review_team', 'participants'], updated);
  };

  const answeredFactors = EXCLUSIONARY_FACTORS.filter(f => content.exclusionary_factors?.[f.key]?.value).length;

  return (
    <div className="max-w-4xl space-y-8" data-testid="permission-editor">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-outfit text-xl font-semibold text-[#1C1C1C]" data-testid="permission-title">Permission To Evaluate</h2>
          <p className="text-sm text-[#595959] mt-1">{section?.description}</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#5A8A5A] hover:bg-[#4A7A4A] text-white rounded-xl shrink-0" data-testid="permission-save-top">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      {/* Exclusionary Factors */}
      <CollapsibleGroup title={`Exclusionary Factors (${answeredFactors}/${EXCLUSIONARY_FACTORS.length} addressed)`}>
        <div className="bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden" data-testid="pte-exclusionary">
          <div className="px-5 py-3 bg-[#F8F6F2] border-b border-[#E5E5E5]">
            <p className="text-xs text-[#595959]">Indicate whether each exclusionary factor applies. These must be addressed as part of the eligibility determination.</p>
          </div>
          <div className="divide-y divide-[#F3F4F1]">
            {EXCLUSIONARY_FACTORS.map((factor, idx) => {
              const data = content.exclusionary_factors?.[factor.key] || { value: '', clarification: '' };
              return (
                <div key={factor.key} className="px-5 py-4" data-testid={`pte-exclusion-${factor.key}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <span className="text-xs font-bold text-[#5A8A5A] bg-[#5A8A5A]/10 rounded-full w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">{idx + 1}</span>
                      <span className="text-sm text-[#1C1C1C]">{factor.label}</span>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <button className={`px-3 py-1.5 rounded-l-lg text-xs font-medium border transition-colors ${data.value === 'yes' ? 'bg-red-500 text-white border-red-500' : 'bg-white text-[#595959] border-[#E5E5E5] hover:bg-red-50'}`}
                        onClick={() => update(['exclusionary_factors', factor.key, 'value'], data.value === 'yes' ? '' : 'yes')}
                        data-testid={`pte-exclusion-yes-${factor.key}`}>Yes</button>
                      <button className={`px-3 py-1.5 rounded-r-lg text-xs font-medium border transition-colors ${data.value === 'no' ? 'bg-[#5A8A5A] text-white border-[#5A8A5A]' : 'bg-white text-[#595959] border-[#E5E5E5] hover:bg-green-50'}`}
                        onClick={() => update(['exclusionary_factors', factor.key, 'value'], data.value === 'no' ? '' : 'no')}
                        data-testid={`pte-exclusion-no-${factor.key}`}>No</button>
                    </div>
                  </div>
                  {data.value && (
                    <div className="mt-2 ml-8">
                      <Input value={data.clarification || ''} onChange={e => update(['exclusionary_factors', factor.key, 'clarification'], e.target.value)}
                        placeholder="Optional clarification..." className="rounded-xl border-[#E5E5E5] text-xs h-8"
                        data-testid={`pte-exclusion-note-${factor.key}`} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </CollapsibleGroup>

      {/* Additional Data Needed */}
      <CollapsibleGroup title="Additional Data Needed">
        <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="pte-additional-data">
          <Textarea value={content.additional_data_needed || ''} onChange={e => update(['additional_data_needed'], e.target.value)}
            placeholder="Identify any additional data, evaluations, or information the team needs..."
            rows={3} className="rounded-xl border-[#E5E5E5] text-sm" data-testid="pte-additional-data-input" />
        </div>
      </CollapsibleGroup>

      {/* Proposed Evaluation Areas */}
      <CollapsibleGroup title="Proposed Evaluation Areas">
        <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5 space-y-3" data-testid="proposed-areas-section">
          {(content.proposed_areas || []).map((area, idx) => (
            <div key={idx} className="flex items-center gap-2" data-testid={`proposed-area-${idx}`}>
              <Input value={area} onChange={e => updateArea(idx, e.target.value)}
                placeholder="e.g., Cognitive / Intelligence" className="rounded-xl border-[#E5E5E5] text-sm h-9 flex-1" />
              <button onClick={() => removeArea(idx)} className="p-1 rounded hover:bg-red-50"><Trash2 className="h-3.5 w-3.5 text-red-400" /></button>
            </div>
          ))}
          <Button variant="outline" size="sm" className="rounded-lg text-xs h-7" onClick={addArea} data-testid="add-proposed-area">
            <Plus className="h-3 w-3 mr-1" />Add Area
          </Button>
        </div>
      </CollapsibleGroup>

      {/* Parental Consent */}
      <CollapsibleGroup title="Parental Consent">
        <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="parental-consent-section">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Consent Status</label>
              <Select value={content.parental_consent?.status || ''} onValueChange={v => update(['parental_consent', 'status'], v)}>
                <SelectTrigger className="rounded-xl text-sm h-9" data-testid="consent-status"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{CONSENT_OPTIONS.map(o => <SelectItem key={o} value={o} className="capitalize">{o.charAt(0).toUpperCase() + o.slice(1)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Date</label>
              <Input type="date" value={content.parental_consent?.date || ''} onChange={e => update(['parental_consent', 'date'], e.target.value)} className="rounded-xl border-[#E5E5E5] text-sm h-9" data-testid="consent-date" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Notes</label>
              <Input value={content.parental_consent?.notes || ''} onChange={e => update(['parental_consent', 'notes'], e.target.value)} placeholder="Optional notes" className="rounded-xl border-[#E5E5E5] text-sm h-9" />
            </div>
          </div>
        </div>
      </CollapsibleGroup>

      {/* Procedural Safeguards */}
      <CollapsibleGroup title="Procedural Safeguards">
        <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="safeguards-section">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Provided?</label>
              <button className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${content.procedural_safeguards?.provided ? 'bg-[#5A8A5A] text-white border-[#5A8A5A]' : 'bg-white text-[#595959] border-[#E5E5E5] hover:bg-green-50'}`}
                onClick={() => update(['procedural_safeguards', 'provided'], !content.procedural_safeguards?.provided)}
                data-testid="safeguards-toggle">
                {content.procedural_safeguards?.provided ? 'Yes' : 'No'}
              </button>
            </div>
            <div>
              <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Date</label>
              <Input type="date" value={content.procedural_safeguards?.date || ''} onChange={e => update(['procedural_safeguards', 'date'], e.target.value)} className="rounded-xl border-[#E5E5E5] text-sm h-9" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Method</label>
              <Input value={content.procedural_safeguards?.method || ''} onChange={e => update(['procedural_safeguards', 'method'], e.target.value)} placeholder="e.g., Hand delivered" className="rounded-xl border-[#E5E5E5] text-sm h-9" />
            </div>
          </div>
        </div>
      </CollapsibleGroup>

      {/* Evaluation Review Team */}
      <CollapsibleGroup title="Evaluation Review Team">
        <div className="bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden" data-testid="pte-review-team">
          <div className="flex items-center justify-between px-5 py-3.5 bg-[#F3F4F1]">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4 text-[#5A8A5A]" />
              <span className="font-medium text-sm text-[#1C1C1C]">Participants ({content.evaluation_review_team?.participants?.length || 0})</span>
            </div>
            <Button variant="outline" size="sm" className="rounded-lg text-xs h-7" onClick={addParticipant} data-testid="add-pte-participant">
              <Plus className="h-3 w-3 mr-1" />Add Member
            </Button>
          </div>
          <div className="p-5">
            {(content.evaluation_review_team?.participants || []).length === 0 ? (
              <p className="text-sm text-[#595959] text-center py-4">No team members added yet.</p>
            ) : (
              <div className="space-y-3">
                {content.evaluation_review_team.participants.map((p, idx) => (
                  <div key={p.id || idx} className="border border-[#E5E5E5] rounded-xl p-4 relative group" data-testid={`pte-team-member-${idx}`}>
                    <button onClick={() => removeParticipant(idx)} className="absolute top-3 right-3 p-1 rounded hover:bg-red-50 opacity-0 group-hover:opacity-100 transition" data-testid={`remove-pte-participant-${idx}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-400" />
                    </button>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div>
                        <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Name</label>
                        <Input value={p.name} onChange={e => updateParticipant(idx, 'name', e.target.value)} placeholder="Full name" className="rounded-xl border-[#E5E5E5] text-sm h-9" />
                      </div>
                      <div>
                        <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Role</label>
                        <Select value={p.role || ''} onValueChange={v => updateParticipant(idx, 'role', v)}>
                          <SelectTrigger className="rounded-xl text-sm h-9"><SelectValue placeholder="Select role" /></SelectTrigger>
                          <SelectContent>{TEAM_ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Title / Credentials</label>
                        <Input value={p.title} onChange={e => updateParticipant(idx, 'title', e.target.value)} placeholder="e.g., M.Ed., CCC-SLP" className="rounded-xl border-[#E5E5E5] text-sm h-9" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </CollapsibleGroup>

      <div className="flex justify-end pt-4 border-t border-[#E5E5E5]">
        <Button onClick={handleSave} disabled={saving} className="bg-[#5A8A5A] hover:bg-[#4A7A4A] text-white rounded-xl" data-testid="permission-save-bottom">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Permission To Evaluate'}
        </Button>
      </div>
    </div>
  );
}
